import { addDays } from './mirror.js'
import { MID_TERM_MINIMUM_NIGHTS, VERDICT, verdictFor } from './search.js'

/**
 * WHICH DAYS SHE CAN ACTUALLY PICK — worked out before she asks, not after (mtr#214).
 *
 * The Product Owner, 2026-09-29, after sending a real enquiry from the public page:
 *
 *   "The page does not show which dates are available. If they are not, it says 'try other dates'
 *    — after two or three tries a renter loses interest. It should show which dates can be
 *    chosen. A move-in day should only be offered if the 30 days after it are free."
 *
 * He is right, and the market agrees: Airbnb, Furnished Finder and Zillow all show what is free
 * BEFORE the renter commits to a guess. Two empty date boxes and a "no" afterwards is a game of
 * twenty questions where every wrong answer costs a renter.
 *
 * NOTHING NEW IS DECIDED HERE. Every "can this stay happen" question is still answered by
 * `verdictFor` — the module measured against the live portfolio, which knows that a booking window
 * is not a supply fact, that a missing night is not an empty one, and that a departure day off the
 * edge is a caveat rather than a refusal. This file only ASKS IT REPEATEDLY, once per candidate
 * day, and collects the answers. A second opinion about availability is the one thing this feature
 * must not introduce.
 *
 * WHAT COUNTS AS A DAY SHE MAY PICK
 *
 *   FITS            → yes.
 *   NEEDS_OWNER_OK  → yes, and separately, because the page already has a sentence for it. Those
 *                     are free, quotable nights behind Guesty's advance-booking wall; the Product
 *                     Owner settled on 2026-09-04 that a mid-term enquiry that far out is a
 *                     CONDITION on a quote, not a refusal. Hiding them puts 34 homes back behind a
 *                     setting.
 *   everything else → not offered. UNKNOWN especially: "nobody has looked" is not "it is free",
 *                     and #88's rule is that we say available only about nights we know are free.
 *
 * WHY IT IS CAPPED, AND WHY THE CAP IS HONEST. A calendar can only offer what the mirror can speak
 * for. `lastDayWeCanSpeakFor` comes back with the answer so the screen can say "beyond here, ask
 * us" instead of drawing empty months that a renter would read as "all booked".
 */

/**
 * The shortest stay THIS home will actually take, on this day.
 *
 * WHY THIS IS NOT JUST 30. A home whose own `minNights` is 45 refuses every 30-night probe, so
 * probing it at 30 would report the home as having no move-in days at all — a silent blackout of a
 * whole property, which is exactly the class of bug #214 exists to kill. The floor is the longer of
 * the two rules, because both are real: ours makes the stay mid-term, theirs is the owner's.
 */
function shortestStayOn(unit, mirror, day, floor) {
  const found = mirror.lookup(unit.id, day)
  const ownFloor = typeof found?.day?.minNights === 'number' ? found.day.minNights : 0
  return Math.max(floor, ownFloor)
}

/**
 * True when the only thing wrong is the LENGTH or the ONE departure day we asked about — so a
 * different length from the same move-in day may still be fine.
 *
 * `maxNights` and a closed arrival are the opposite: they refuse every longer stay from this day,
 * and treating them as "try again" would walk a loop through a year of certain refusals.
 */
const onlyThisLengthIsWrong = (verdict) =>
  verdict.restrictions.length > 0 &&
  verdict.restrictions.every((r) => r.rule === 'ctd' || r.rule === 'minNights')

/**
 * The move-in days that work, for one home.
 *
 * `days` is how far ahead to look. 180 is two academic terms and one relocation cycle — far enough
 * that a renter planning a spring move finds her day, and past it the booking window makes almost
 * everything conditional anyway, which the page says in words rather than by drawing six more
 * months a renter cannot use.
 */
export function moveInDays(unit, mirror, {
  from,
  days = 180,
  minNights = MID_TERM_MINIMUM_NIGHTS,
} = {}) {
  if (!from) throw new TypeError('moveInDays needs `from` — nothing here reads the clock')
  if (!unit?.id) throw new TypeError('moveInDays needs a unit with an id')

  const open = []
  const needsOwnerOk = []
  let lastDayWeCanSpeakFor = null

  for (let i = 0; i < days; i++) {
    const checkIn = addDays(from, i)
    const nights = shortestStayOn(unit, mirror, checkIn, minNights)
    const checkOut = addDays(checkIn, nights)

    let verdict
    try {
      verdict = verdictFor(unit, mirror, { checkIn, checkOut, midTermMinimum: minNights })
    } catch {
      /* A window the mirror cannot be asked about is not a day we offer. */
      continue
    }

    if (verdict.verdict === VERDICT.FITS) {
      open.push(checkIn)
      lastDayWeCanSpeakFor = checkIn
    } else if (verdict.verdict === VERDICT.NEEDS_OWNER_OK || verdict.verdict === VERDICT.NOT_YET_OPEN) {
      needsOwnerOk.push(checkIn)
      lastDayWeCanSpeakFor = checkIn
    } else if (verdict.verdict !== VERDICT.UNKNOWN) {
      /*
        A real "no" — let, or refused by the home's own rules — is still a day we can SPEAK for.
        Only UNKNOWN leaves the cap where it was, because UNKNOWN is the one answer that means we
        have run out of calendar.
      */
      lastDayWeCanSpeakFor = checkIn
    }
  }

  return { open, needsOwnerOk, lastDayWeCanSpeakFor }
}

/**
 * Once she has picked a move-in day, the days she may leave on.
 *
 * THE FIRST ONE IS `minNights` LATER, NOT `minNights + 1`. The checkout date is neither occupied
 * nor billable (PRICING_RULES P-10), so a thirty-night stay starting on the 1st leaves on the 31st.
 * Getting this off by one would put every renter's lease a day out.
 */
export function moveOutDays(unit, mirror, {
  moveIn,
  days = 365,
  minNights = MID_TERM_MINIMUM_NIGHTS,
} = {}) {
  if (!moveIn) throw new TypeError('moveOutDays needs the move-in day she picked')
  if (!unit?.id) throw new TypeError('moveOutDays needs a unit with an id')

  const open = []
  const needsOwnerOk = []

  for (let n = minNights; n <= days; n++) {
    const checkOut = addDays(moveIn, n)
    let verdict
    try {
      verdict = verdictFor(unit, mirror, { checkIn: moveIn, checkOut, midTermMinimum: minNights })
    } catch {
      break
    }

    if (verdict.verdict === VERDICT.FITS) {
      open.push(checkOut)
    } else if (verdict.verdict === VERDICT.NEEDS_OWNER_OK || verdict.verdict === VERDICT.NOT_YET_OPEN) {
      needsOwnerOk.push(checkOut)
    } else if (verdict.verdict === VERDICT.RESTRICTED && onlyThisLengthIsWrong(verdict)) {
      /*
        This ONE departure day is closed, or this length is below the home's own minimum. Neither
        says anything about a longer stay, so we keep walking. Skipping the day is the whole
        correction: it never enters the list.
      */
      continue
    } else {
      /*
        THE FIRST NIGHT WE CANNOT PROMISE ENDS THE LIST.

        Not `continue`: a stay cannot step over a night somebody else is in, so every longer stay
        from this move-in day is refused for the same reason. Carrying on would draw a calendar
        with holes that a renter would read as "these are free" — the exact lie this whole feature
        exists to stop. The same goes for UNKNOWN: past the edge of the mirror there is nothing
        further to learn, only further to guess.
      */
      break
    }
  }

  return { open, needsOwnerOk }
}
